import React, { useEffect, useState } from "react";
import { BeatLoader } from "react-spinners";
import { motion } from "framer-motion";
import { useFetchLocations } from "../../../hooks/useFetchLocations";
import { usePageSettings } from "../../../contexts/PageSettingsContext";
import LocationListBox from "../inputs/LocationListbox";
import { useSelectedPlanContext } from "../../../contexts/SelectedPlanContext";
import PrimaryButton from "../inputs/buttons/Button";
import { useQuery } from "@tanstack/react-query";
import { FaCheckCircle } from "react-icons/fa";
import useFetchColocationResources from "../../../hooks/useFetchColocationResources";
import { useFetchPrice } from "../../../hooks/useFetchPrice";
import InputTableTitle from "./InputTableTitle";
import ErrorMessage from "../ErrorMessage";
import InputTableContactUs from "./InputTableContactUs";
import useFetchColoRestrictedIds from "../../../hooks/useFetchRestrictedIds";
import useDefaultSelections from "../../../hooks/useDefaultSelections";
import InputWithOptions from "../inputs/InputWithOptions";
import { constructUrl } from "../../../utils/constructUrl";
import { navigate } from "gatsby";
import { getTranslatedCityName } from "../../../utils/getTranslatedCityName";

const InputTableColocation = ({ serviceType, translations, defaultIataCode, pageContext }) => {
	const { selectedLocation, pageSettingsContext } = usePageSettings();
	const { selectedPlan, changeSelectedPlan } = useSelectedPlanContext();

	const { city, language, country, hasCityInUrl, isDf, isHomepage } = pageSettingsContext;

	let citySlug = hasCityInUrl ? getTranslatedCityName(language, city) : null;
	let countrySlug = isDf ? "df" : isHomepage ? null : country;

	// Fetch restricted IDs (TODO LOADING / ERROR STATE FOR HIGHLIGHTS AND ID/DEPENDENCY VALIDATION)
	const {
		data: restrictedIds,
		isLoading: loadingRestrictedIds,
		isError: errorRestrictedIds,
		refetch: refetchRestrictedIds,
	} = useFetchColoRestrictedIds(
		selectedPlan?.rack?.id || null,
		selectedPlan?.bandwidth?.id || null,
		selectedPlan?.energy?.id || null,
		selectedPlan?.prefix?.id || null
	);

	const [locationFeatures, setLocationFeatures] = useState([]);

	const locationHighlightsQuery = useQuery({
		queryKey: ["locationHighlights", selectedLocation.iata_code],
		queryFn: () => fetchLocationHighlights(selectedLocation.iata_code),
		enabled: !!selectedLocation.iata_code,
	});

	useEffect(() => {
		changeSelectedPlan({
			service: "colocation"
		});
	}, [selectedLocation.iata_code]);

	const fetchLocationHighlights = async (iataCode) => {
		try {
			const response = await fetch(`${process.env.GATSBY_HIGHLIGHTS_COLOCATION_ENDPOINT}/${iataCode}/highlights`);

			if (response.ok) {
				const data = await response.json();

				if (Array.isArray(data)) {
					const features = data.map((feature) => feature);

					setLocationFeatures(features || []);
					return features;
				} else {
					console.error("Invalid data format received from the API");
				}
			} else {
				console.error("Failed to fetch location highlights");
			}
		} catch (error) {
			console.error("Error fetching location highlights:", error);
		}
	};

	useEffect(() => {
		if (selectedLocation.iata_code) {
			locationHighlightsQuery.refetch();
		}
	}, [selectedLocation.iata_code]);

	const queryLocations = useFetchLocations(
		serviceType,
		selectedLocation.iata_code !== "" ? selectedLocation.iata_code : defaultIataCode
	);

	const queryResources = useFetchColocationResources(
		selectedLocation.iata_code !== "" ? selectedLocation.iata_code : defaultIataCode
	);

	const queryPrice = useFetchPrice("colocation");

	// Use the custom hook to handle default selections
	useDefaultSelections(
		queryResources.data,
		restrictedIds,
		queryResources.isSuccess && !queryResources.isFetching,
		!loadingRestrictedIds
	);

	const motionContainer = {
		hidden: { opacity: 0 },
		visible: { opacity: 1 },
	};

	const motionItem = {
		hidden: { opacity: 0, y: 20 },
		visible: { opacity: 1, y: 0 },
	};

	const handleOrderNow = (event) => {
		event.preventDefault();
		// Construct the complete target URL using pageContext and constructUrl method
		const translatedCheckoutUrl = constructUrl(language, countrySlug, citySlug, "/checkout");
		sessionStorage.removeItem("checkout-tab");
		navigate(translatedCheckoutUrl);
	};

	const isLoadingPrice = queryLocations.isFetching || queryResources.isFetching || queryPrice.isFetching;

	return (
		<section className="container overflow-visible text-ipxon-black mx-auto flex flex-col gap-6 pb-20 w-full md:px-28 xl:px-0 px-4">
			<InputTableTitle
				serviceType={serviceType}
				selectedLocation={selectedLocation}
				translations={translations}
			/>

			{queryResources.isError ? (
				<div className="flex justify-center bg-gray-50 min-h-[16.875rem] py-8 px-6 rounded-lg w-full">
					<ErrorMessage translations={translations} />
				</div>
			) : (
				<div
					className={`flex flex-col xl:flex-row bg-gray-50 min-h-[16.875rem] py-8 px-6 gap-10 rounded-lg justify-between w-full`}
				>
					<div className={`flex flex-col justify-start gap-8`}>
						<div className="flex flex-col justify-center items-center uppercase gap-4">
							<h4 className="flex self-start uppercase font-bold text-xs">
								{translations["LabelForLocationListBox"]}
							</h4>
							<LocationListBox
								color="white"
								locationList={queryLocations.isFetching ? [] : queryLocations.data}
								translations={translations}
							/>
						</div>
						{locationFeatures && (
							<div className="w-full flex flex-col gap-4 xl:min-h-[136px]">
								<h4 className="flex uppercase font-bold text-xs">{translations["Features"]}</h4>
								<ul className="flex flex-col h-full gap-2">
									{locationFeatures.slice(0, 4).map((feature) => (
										<li key={feature} className="flex gap-2 items-center">
											<FaCheckCircle className="h-3 w-3 text-[#B9BFC1]" />
											<p className="text-sm">{feature}</p>
										</li>
									))}
								</ul>
							</div>
						)}
					</div>

					<div className="w-full flex flex-col xl:flex-row gap-4 xl:gap-3 2xl:gap-4 justify-around">
						{queryResources.isFetching ? (
							<span className="flex self-center justify-center w-full h-full items-center">
								<BeatLoader color="#d1d5db" size={16} />
							</span>
						) : (
							queryResources.isSuccess && (
								<>
									<InputWithOptions
										input="RACK"
										resources={queryResources.data.racks ? queryResources.data.racks : []}
										restrictedIds={restrictedIds ? restrictedIds : []}
										translations={translations}
										vertical={true}
									/>
									<InputWithOptions
										input="ENERGY"
										resources={queryResources.data.energies ? queryResources.data.energies : []}
										restrictedIds={restrictedIds ? restrictedIds : []}
										translations={translations}
										vertical={true}
									/>
									<InputWithOptions
										input="BANDWIDTH"
										resources={queryResources.data.bandwidths ? queryResources.data.bandwidths : []}
										restrictedIds={restrictedIds ? restrictedIds : []}
										translations={translations}
										vertical={true}
									/>
									<InputWithOptions
										input="PREFIX"
										resources={queryResources.data.prefixes ? queryResources.data.prefixes : []}
										restrictedIds={restrictedIds ? restrictedIds : []}
										translations={translations}
										vertical={true}
									/>
								</>
							)
						)}
					</div>
					<div
						className={`xl:min-w-[300px] xl:w-4/12 border-t xl:border-t-0 pt-8 md:py-8 xl:py-0 xl:border-l border-gray-300 flex flex-col justify-center items-center font-bold uppercase gap-4`}
					>
						<div className="flex flex-col justify-center items-center text-3xl md:text-2xl 2xl:text-3xl gap-3">
							<motion.p
								initial="hidden"
								animate="visible"
								exit="hidden"
								variants={motionContainer}
								className="flex gap-1 h-7 items-center justify-center"
							>
								{isLoadingPrice ? (
									<BeatLoader color="#d1d5db" />
								) : (
									<motion.span variants={motionItem}>{queryPrice.data}</motion.span>
								)}
							</motion.p>
							<p className="text-gray-400 font-light text-center text-sm" key="payment">
								{translations["InputTableTimeSpan"]}
							</p>
						</div>
						<PrimaryButton
							onClick={handleOrderNow}
							className={`button-magenta flex reflect-below min-w-[175px] gap-1 h-8`}
						>
							{translations["InputTableButtonText"]}
						</PrimaryButton>
					</div>
				</div>
			)}

			<InputTableContactUs
				text={translations["InputTableDisclaimer"]}
				linkText={translations["InputTableDisclaimerLink"]}
				pageContext={pageContext}
			/>
		</section>
	);
};

export default InputTableColocation;

